/** Small, deliberately non-HTML news format shared by native rendering and email. */
export type NewsSpan = { text: string; emphasis?: 'bold' | 'italic'; href?: string }
export type NewsBlock = { kind: 'h1' | 'h2' | 'bullet' | 'paragraph'; spans: NewsSpan[] }

export function safeNewsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return value.startsWith('https://') && url.protocol === 'https:' && !!url.hostname && !url.username && !url.password && !/[\s<>"\\]/.test(value)
  } catch { return false }
}

/** Only known main-app destinations are routed internally; other URLs stay external. */
export function newsAppRoute(href: string): string | null {
  if (!safeNewsUrl(href)) return null
  const url = new URL(href)
  if (!['warcarp.com', 'www.warcarp.com'].includes(url.hostname) || url.port) return null
  const path = url.pathname.replace(/\/$/, '') || '/'
  if (!(/^\/(?:tutorial|leaderboard|games(?:\/[^/]+(?:\/(?:chat|players|rules|events|map-details))?)?|maps(?:\/[^/]+)?|join\/[^/]+|u\/[^/]+|settings(?:\/(?:contact|inbox(?:\/[^/]+)?|about|rules|notifications|account))?)$/.test(path) || path === '/')) return null
  return `${path}${url.search}${url.hash}`
}

export function parseNews(body: string): NewsBlock[] {
  return body.replace(/\r\n?/g, '\n').split('\n').map(line => {
    const prefix = /^(## |# |- )/.exec(line)?.[0]
    const text = prefix ? line.slice(prefix.length) : line
    const spans: NewsSpan[] = []
    const pattern = /\[([^\]\n]+)\]\(([^\s()]+)\)|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g
    let end = 0
    for (const match of text.matchAll(pattern)) {
      if (match.index > end) spans.push({ text: text.slice(end, match.index) })
      if (match[1] !== undefined) spans.push(safeNewsUrl(match[2]!) ? { text: match[1], href: match[2]! } : { text: match[0] })
      else spans.push({ text: match[3] ?? match[4]!, emphasis: match[3] ? 'bold' : 'italic' })
      end = match.index + match[0].length
    }
    if (end < text.length) spans.push({ text: text.slice(end) })
    return { kind: prefix === '# ' ? 'h1' : prefix === '## ' ? 'h2' : prefix === '- ' ? 'bullet' : 'paragraph', spans }
  })
}

export function newsPlainText(body: string): string {
  return parseNews(body).map(block => `${block.kind === 'bullet' ? '• ' : ''}${block.spans.map(span => span.href ? `${span.text} (${span.href})` : span.text).join('')}`).join('\n')
}

const escapeNewsHtml = (value: string) => value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!)

export function newsHtml(body: string): string {
  const blocks = parseNews(body).filter(block => block.spans.length > 0)
  const output: string[] = []
  for (let index = 0; index < blocks.length; index++) {
    const block = blocks[index]!
    if (block.kind === 'bullet') {
      const items: string[] = []
      while (index < blocks.length && blocks[index]!.kind === 'bullet') {
        const item = blocks[index++]!
        items.push(`<li>${item.spans.map(span => {
          const text = escapeNewsHtml(span.text)
          if (span.href) return `<a href="${escapeNewsHtml(span.href)}" rel="noopener noreferrer">${text}</a>`
          return span.emphasis === 'bold' ? `<strong>${text}</strong>` : span.emphasis === 'italic' ? `<em>${text}</em>` : text
        }).join('')}</li>`)
      }
      index--
      output.push(`<ul class="wc-news-list">${items.join('')}</ul>`)
      continue
    }
    const tag = block.kind === 'h1' || block.kind === 'h2' ? block.kind : 'p'
    const content = block.spans.map(span => {
      const text = escapeNewsHtml(span.text)
      if (span.href) return `<a href="${escapeNewsHtml(span.href)}" rel="noopener noreferrer">${text}</a>`
      return span.emphasis === 'bold' ? `<strong>${text}</strong>` : span.emphasis === 'italic' ? `<em>${text}</em>` : text
    }).join('')
    output.push(`<${tag}>${content}</${tag}>`)
  }
  return output.join('\n')
}
